import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import {
    useGetIncomingPostbackExportQuery,
    useUpdateIncomingPostbackMutation,
} from "api/TenantSlug/IncomingPostback/hooks.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyPostbackIncomingFormData } from "./formSchema";

import IncomingPostbackData = App.Data.IncomingPostbackData;
import UpdateIncomingPostbackRequest = App.Http.Requests.Tracking.UpdateIncomingPostbackRequest;

interface ApiToolGeneralFormProps {
    incomingPostback: IncomingPostbackData;
    onClose: () => void;
}

const ModifyPostbackIncomingFormContent: FC<ApiToolGeneralFormProps> = ({ incomingPostback, onClose }) => {
    const { t } = usePaldockTranslation("forms.modify-postback-incoming");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyPostbackIncomingFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateIncomingPostbackMutation = useUpdateIncomingPostbackMutation(incomingPostback.id ?? "");

    const onSubmitForm = (data: ModifyPostbackIncomingFormData) => {
        const payload: UpdateIncomingPostbackRequest = JSON.parse(data.incomingPostback);

        updateIncomingPostbackMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(t("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getIncomingPostback", incomingPostback.id] });
                queryClient.refetchQueries({ queryKey: ["getIncomingPostbackExport", incomingPostback.id] });
                onClose();
            },
            onError: () => {
                toast.error(t("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem
                control={control}
                name={fieldNames.incomingPostback?.name || "incomingPostback"}
                label={t("fields.incoming_postback.label")}
            >
                <Input.TextArea rows={4} placeholder={t("fields.incoming_postback.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolTrackingIncomingPostbackExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyPostbackIncomingForm: FC<ApiToolGeneralFormProps> = ({ incomingPostback, onClose }) => {
    const getIncomingPostbackExportQuery = useGetIncomingPostbackExportQuery(incomingPostback?.id || "");
    const data = getIncomingPostbackExportQuery.data;

    if (!incomingPostback || !data) return null;
    return (
        <GeneralFormProvider<ModifyPostbackIncomingFormData>
            schema={formSchema}
            id={FormId.TrackingToolFromExportForm}
            defaultValues={{
                incomingPostback: JSON.stringify(data.incomingPostback),
            }}
        >
            <ModifyPostbackIncomingFormContent incomingPostback={incomingPostback} onClose={onClose} />
        </GeneralFormProvider>
    );
};
